import React from 'react';

/**
 * EmailResponseModal - Display detailed email response information
 * Shows: Pending, Sent, Open status and YES/NO response
 */
const EmailResponseModal = ({ isOpen, onClose, record }) => {
  if (!isOpen || !record) return null;

  const emailResponse = record.email_response; // 'yes', 'no', or null
  const emailStageStatus = record.email_stage_status; // 'pass', 'reject', 'pending'
  const emailResponseAt = record.email_response_at;
  const emailed = record.emailed; // boolean
  const emailOpened = record.email_opened; // boolean
  const emailOpenedAt = record.email_opened_at;

  // Determine email status: Pending, Sent, or Open
  const getEmailStatus = () => {
    if (!emailed) return { status: 'PENDING', text: 'Not Sent', color: 'gray', icon: '⏳', bg: 'bg-gray-500/20', border: 'border-gray-500/50' };
    if (emailOpened) return { status: 'OPEN', text: 'Opened', color: 'blue', icon: '📬', bg: 'bg-blue-500/20', border: 'border-blue-500/50' };
    if (emailed) return { status: 'SENT', text: 'Sent', color: 'yellow', icon: '📧', bg: 'bg-yellow-500/20', border: 'border-yellow-500/50' };
    return { status: 'PENDING', text: 'Pending', color: 'gray', icon: '⏳', bg: 'bg-gray-500/20', border: 'border-gray-500/50' };
  };

  // Get YES/NO response status
  const getResponseStatus = () => {
    if (emailResponse === 'yes') return { text: 'YES', color: 'green', icon: '✅', bg: 'bg-green-500/20', border: 'border-green-500/50' };
    if (emailResponse === 'no') return { text: 'NO', color: 'red', icon: '❌', bg: 'bg-red-500/20', border: 'border-red-500/50' };
    return { text: 'NO RESPONSE', color: 'gray', icon: '⏳', bg: 'bg-gray-500/20', border: 'border-gray-500/50' };
  };

  // Get stage status
  const getStageStatus = () => {
    if (emailStageStatus === 'pass') return { text: 'PASS', color: 'green', bg: 'bg-green-900/40', border: 'border-green-500/50' };
    if (emailStageStatus === 'reject') return { text: 'REJECT', color: 'red', bg: 'bg-red-900/40', border: 'border-red-500/50' };
    return { text: 'PENDING', color: 'yellow', bg: 'bg-yellow-900/40', border: 'border-yellow-500/50' };
  };

  const emailStatus = getEmailStatus();
  const responseStatus = getResponseStatus();
  const stageStatus = getStageStatus();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-[#0d1117] border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#161b22] to-[#0d1117] px-6 py-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Status Badge */}
              <div className={`px-3 py-1.5 rounded-lg font-bold text-sm flex items-center gap-2 ${stageStatus.bg} ${stageStatus.border} border`}>
                <span>{responseStatus.icon}</span>
                <span className={`text-${stageStatus.color}-400`}>{stageStatus.text}</span>
              </div>
              
              {/* Person Info */}
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {record?.first_name} {record?.last_name}
                </h2>
                <p className="text-xs text-gray-400">{record?.email || record?.business_email}</p>
              </div>
            </div>
            
            <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-6">
          <div className="space-y-4">
            {/* Email Status: Pending, Sent, Open */}
            <div className={`bg-[#161b22] rounded-lg border ${emailStatus.border} p-4`}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
                <span>📧</span> Email Status
              </h3>
              <div className={`p-4 rounded-lg ${emailStatus.bg} border ${emailStatus.border}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{emailStatus.icon}</span>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Current Status</p>
                      <p className={`text-xl font-bold text-${emailStatus.color}-400`}>
                        {emailStatus.text}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Status Timeline */}
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className={`p-2 rounded text-center ${emailed ? 'bg-green-500/20 text-green-400' : 'bg-gray-800/50 text-gray-500'}`}>
                    <p className="text-xs font-medium">Sent</p>
                    <p className="text-lg">{emailed ? '✓' : '-'}</p>
                  </div>
                  <div className={`p-2 rounded text-center ${emailOpened ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800/50 text-gray-500'}`}>
                    <p className="text-xs font-medium">Opened</p>
                    <p className="text-lg">{emailOpened ? '✓' : '-'}</p>
                  </div>
                  <div className={`p-2 rounded text-center ${emailResponse ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800/50 text-gray-500'}`}>
                    <p className="text-xs font-medium">Responded</p>
                    <p className="text-lg">{emailResponse ? '✓' : '-'}</p>
                  </div>
                </div>

                {/* Timestamps */}
                {emailOpenedAt && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-[10px] text-gray-500">Opened At:</p>
                    <p className="text-xs text-gray-400">{new Date(emailOpenedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>

            {/* YES/NO Response */}
            <div className={`bg-[#161b22] rounded-lg border ${responseStatus.border} p-4`}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
                <span>💬</span> Response
              </h3>
              
              <div className={`p-4 rounded-lg ${responseStatus.bg} border ${responseStatus.border}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{responseStatus.icon}</span>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">User Response</p>
                      <p className={`text-2xl font-bold text-${responseStatus.color}-400`}>
                        {responseStatus.text}
                      </p>
                    </div>
                  </div>
                </div>

                {emailResponse && (
                  <div className="mt-4 p-3 bg-gray-800/50 rounded-lg">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">Response:</span>
                        <p className={`text-sm font-bold mt-1 text-${responseStatus.color}-400 capitalize`}>
                          {emailResponse}
                        </p>
                      </div>
                      {emailResponseAt && (
                        <div>
                          <span className="text-gray-500">Responded At:</span>
                          <p className="text-sm text-gray-300 mt-1">{new Date(emailResponseAt).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!emailResponse && emailed && (
                  <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <p className="text-xs text-yellow-400">
                      ⏳ Email sent but no response received yet
                    </p>
                  </div>
                )}

                {!emailed && (
                  <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <p className="text-xs text-gray-400">
                      Email has not been sent to this record
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Stage Status */}
            <div className={`bg-[#161b22] rounded-lg border ${stageStatus.border} p-4`}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">Stage Status</h3>
              <div className={`p-4 rounded-lg ${stageStatus.bg} text-center`}>
                <div className="text-3xl mb-2">
                  {emailStageStatus === 'pass' ? '🎉' : 
                   emailStageStatus === 'reject' ? '❌' : '⏳'}
                </div>
                <div className={`text-xl font-bold text-${stageStatus.color}-400`}>
                  {stageStatus.text}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {emailStageStatus === 'pass' 
                    ? 'Email response indicates positive interest'
                    : emailStageStatus === 'reject'
                    ? 'Email response indicates no interest'
                    : 'Awaiting email response'}
                </p>
              </div>
            </div>

            {/* Record Information */}
            <div className="bg-[#161b22] rounded-lg border border-gray-700 p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase mb-3">Record Information</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <p className="text-gray-300 mt-1">{record?.first_name} {record?.last_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Company:</span>
                  <p className="text-gray-300 mt-1">{record?.company_name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>
                  <p className="text-gray-300 mt-1 truncate">{record?.email || record?.business_email || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Job Title:</span>
                  <p className="text-gray-300 mt-1">{record?.job_title || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-[#161b22] border-t border-gray-700 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailResponseModal;
